import { PickerValue, ScrollPicker } from "@/components/scroll-picker";
import { QueryKeys } from "@/constants/query-keys";
import { useGetUser } from "@/hooks/requests/user/use-get-user";
import { useUpdateUser } from "@/hooks/requests/user/use-update-user";
import { useToast } from "@/hooks/ui/use-toast";
import { useStepRouteInfo } from "@/hooks/use-step-route-info";
import { cn } from "@/utils/cn";
import { IonContent, IonPage } from "@ionic/react";
import { useQueryClient } from "@tanstack/react-query";
import { subYears } from "date-fns";
import { useEffect, useMemo, useRef, useState } from "react";
import { OnboardingNextFab } from "../../components/onboarding-next-fab";
import { OnboardingStepShell } from "../../components/onboarding-step-shell";

const MONTH_LABELS = [
  "jan",
  "feb",
  "mar",
  "apr",
  "may",
  "jun",
  "jul",
  "aug",
  "sep",
  "oct",
  "nov",
  "dec",
];

const pad = (n: number) => String(n).padStart(2, "0");

const buildYearOptions = (min: number, max: number) =>
  Array.from({ length: max - min + 1 }, (_, i) => String(min + i));

const buildDayOptions = (year: number, month: number) =>
  Array.from({ length: new Date(year, month, 0).getDate() }, (_, i) => pad(i + 1));

const MONTH_OPTIONS = MONTH_LABELS.map((_, i) => pad(i + 1));

const renderPickerItem = (value: string, selected: boolean, display?: string) => (
  <div className="flex h-[44px] w-full items-center justify-center px-1 text-[22px] font-semibold">
    <span
      className={cn(
        "flex h-full w-full items-center justify-center",
        selected ? "text-[#43246e]" : "text-[#7485a7]",
      )}
    >
      {display ?? value}
    </span>
  </div>
);

const OnboardingBirthdayPage = () => {
  const { navigateToNextStep, preloadNextRoute } = useStepRouteInfo();
  const { user } = useGetUser();
  const queryClient = useQueryClient();
  const { updateUserAsync, isUpdating } = useUpdateUser();
  const { showError } = useToast();
  const personalInfo = user?.personalInfo;
  const hydratedRef = useRef(false);

  const today = useMemo(() => new Date(), []);
  const maxDate = useMemo(() => subYears(today, 13), [today]);
  const minYear = 1900;
  const maxYear = maxDate.getFullYear();

  const initialDate = useMemo(() => {
    if (personalInfo?.dob) {
      const parsed = new Date(personalInfo.dob);
      if (!isNaN(parsed.getTime())) return parsed;
    }
    return new Date(1995, 0, 1);
  }, [personalInfo?.dob]);

  const [pickerValue, setPickerValue] = useState<PickerValue>({
    year: String(initialDate.getFullYear()),
    month: pad(initialDate.getMonth() + 1),
    day: pad(initialDate.getDate()),
  });

  useEffect(() => {
    if (hydratedRef.current) return;
    if (personalInfo?.dob) {
      const parsed = new Date(personalInfo.dob);
      if (!isNaN(parsed.getTime())) {
        setPickerValue({
          year: String(parsed.getFullYear()),
          month: pad(parsed.getMonth() + 1),
          day: pad(parsed.getDate()),
        });
        hydratedRef.current = true;
      }
    }
  }, [personalInfo?.dob]);

  const yearOptions = useMemo(
    () => buildYearOptions(minYear, maxYear),
    [minYear, maxYear],
  );

  const dayOptions = useMemo(
    () => buildDayOptions(Number(pickerValue.year), Number(pickerValue.month)),
    [pickerValue.year, pickerValue.month],
  );

  const handleSubmit = async () => {
    if (isUpdating) return;
    const dob = `${pickerValue.year}-${pickerValue.month}-${pickerValue.day}`;
    try {
      await Promise.all([
        preloadNextRoute(),
        updateUserAsync({
          firstName: personalInfo?.firstName ?? "",
          lastName: personalInfo?.lastName ?? "",
          dob,
          sex: personalInfo?.sex ?? "",
        }),
      ]);
      queryClient.setQueryData([QueryKeys.USER], (old: object) => ({
        ...old,
        personalInfo: { ...personalInfo, dob },
      }));
      navigateToNextStep();
    } catch (error) {
      showError(error instanceof Error ? error.message : "An unexpected error occurred");
    }
  };

  return (
    <IonPage>
      <IonContent fullscreen>
        <OnboardingStepShell
          icon="/assets/images/onboarding-icons/birthday.svg"
          title="What is your birth date?"
          subtitle="This helps me calculate things more accurately"
        >
          <div className="w-full max-w-[322px] pt-2">
            <ScrollPicker
              value={pickerValue}
              onChange={setPickerValue}
              height={192}
              itemHeight={44}
            >
              <ScrollPicker.Column
                name="day"
                options={dayOptions}
                renderItem={(v, s) => renderPickerItem(v, s)}
              />
              <ScrollPicker.Column
                name="month"
                options={MONTH_OPTIONS}
                renderItem={(v, s) => renderPickerItem(v, s, MONTH_LABELS[Number(v) - 1])}
              />
              <ScrollPicker.Column
                name="year"
                options={yearOptions}
                renderItem={(v, s) => renderPickerItem(v, s)}
              />
            </ScrollPicker>
          </div>
          <OnboardingNextFab onClick={handleSubmit} disabled={isUpdating} />
        </OnboardingStepShell>
      </IonContent>
    </IonPage>
  );
};

export default OnboardingBirthdayPage;
